import React, { useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { userIeCardFetch } from "../../actions/creators/user";
import { getIeCardData, isUserLogged, isUserStuffProcessing } from "../../reducers";
import IePointsBlock from "../checkout/IePointsBlock";
import { dialogAlertEasyClose } from "../../actions/creators/dialogs";
import Error404Page from "../Error404Page";
import ErrorPage from "../ErrorPage";
import FullColumnLayout from "../FullColumnLayout";
import IeCardFront from "./IeCardFront";
import BenefitsListComponent from "./benefits/BenefitsList";
import BenefitDetail from "./benefits/BenefitDetail";
import { loopPackagesRoute } from "../../routes";
import TicketsIcon from "../../assets/svg/tickets";

function getLevelColor(level) {
    switch(level) {
        case 'one': return '#C0BCBD';
        case 'basic': return '#E43651';
        case 'gold': return '#FFBF00';
        default: return '#000000';
    }
}

function getLevelTextColor(level) {
    switch(level) {
        case 'one': return '"FFFFFF"';
        case 'basic': return '#FFFFFF';
        case 'gold': return '#000000';
        default: return '#FFFFFF';
    }
}

const Container = styled.div`
    background: var(--page-bg);
    margin-bottom: -6rem;
    margin-left: auto;
    margin-right: auto;
    max-width: 600px;
    min-height: 100vh;
    padding: 6rem 2rem 10rem;

    &:before {
        width: 200%;
        height: 333px;
        flex-shrink: 0;
        border-radius: 100%;
        background: linear-gradient(180deg, #000 24.95%, #555 87.84%);
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%, -35%);
    }

    body.theme-platinum &:before {
        background: linear-gradient(180deg, var(--primary-b) 35%, var(--primary) 90%);
    }
`;
const Subtitle = styled.p`
    font-family: var(--ff-subtitle);
    font-size: 1.15em;
    margin-bottom: 0;

    body.theme-platinum & {
        color: var(--primary);
        font-weight: 700;
    }
`;
const VisitsCount = styled.span`
    background: ${props => props.color};
    border-radius: 2rem;
    color: white;
    display: block;
    float: right;
    font-size: 0.82em;
    line-height: 2em;
    padding: 0 1.25em;

    & > i { margin-right: 0.5em; }

    body.theme-platinum & {
        background: var(--text-primary) !important;
        color: var(--primary);
        & > i { color: #000; }
    }
`;
const RequiredVisits = styled.ul`
    font-size: 0.9em;
    list-style-type: none;
    margin-bottom: 0;
    padding: 1em 0;

    & > li {
        padding: 0.5em 0;
        position: relative;
    }
`;
const VisitsFlag = styled.span<{level: string}>`
    background: ${props => getLevelColor(props.level)};
    color: ${props => getLevelTextColor(props.level)};
    display: inline-block;
    font-size: 0.7rem;
    height: 2em;
    line-height: 1;
    margin-right: 0.75em;
    padding: 0.55em 0;
    text-align: center;
    vertical-align: middle;
    width: 2em;
`;
const CardButton = styled.div`
    margin-top: 0.5em;
    padding-left: 3em;
    position: relative;

    & > svg, & > img {
        left: 0;
        position: absolute;
        top: 48%;
        transform: translateY(-50%);
    }

    & > i {
        font-size: 1.75em;
        left: 0;
        position: absolute;
        top: 48%;
        transform: translateY(-50%);
    }

    body.theme-platinum & ${Subtitle} { color: var(--text-color); }
`;
const ProgressBar = styled.div`
    margin-top: 2.5em;
    margin-bottom: 2em;
    position: relative;

    & .progress-bar {
        background: #aaa;
        height: 5px;
        overflow: hidden;
        width: 100%;

        body.theme-platinum & { background: #fff; }
    }

    & ${VisitsFlag} {
        position: absolute;
        top: -2em;
        margin-left: -0.25em;
        
        &:nth-child(1) { left: 25%; }
        &:nth-child(2) { left: 50%; }
        &:nth-child(3) { margin-right: 0; right: 0; }
    }
`;

const CardContainer = styled.div`
    body.theme-platinum & {
        background: #fff;
        padding: 1rem 1rem 0;
        position: relative;
    }
`;

const BarCodeImage = styled.div`
    height: 3rem;
    overflow: hidden;

    & > svg {
        width: 100%;
        padding: 0 1rem;
        height: auto;
        
        body.theme-market & #bars { fill: #FFFFFF; }
    }

    body.theme-platinum & { margin-top: -1rem; }
`;
const CardNumber = styled.p`
    font-family: monospace;
    margin-bottom: 1em;
    padding: 0.25em 1rem 1em;
    text-align: center;

    body.theme-platinum & { color: #000; }
`;

interface Benefit {
    active: boolean,
    balance: number,
    balance_expiration_date: string | null,
    balance_expiration_date_string: string | null,
    category: string,
    cinemas: any[],
    description: string,
    display_balance: boolean,
    display_expiration_date: boolean,
    display_regardless_balance: boolean,
    display_renewal_label: boolean,
    external_id: string,
    id: number,
    image: string,
    renewal_label: string | null,
    thumb: string,
    title: string,
}

interface CardData {
    barcode_image: string,
    barcode_image_neg: string,
    barcode_image_png: string,
    barcode_image_png_neg: string,
    basic_required_visits: number,
    benefits: Benefit[],
    card_back: string,
    card_front: string,
    card_number: string,
    cardholder_name: string,
    color: string,
    gold_required_visits: number,
    level: string,
    level_data: {
        name: string,
        description: string
    },
    message: string
    name: string,
    points: {
        available: number,
        id: 'ie' | 'passes',
        messages: string[],
        multiplier: number,
        tickets_limit: number,
        type: 'money' | 'tickets',
        value: number
    }[],
    premium_required_visits: number,
    visits: number,
    error?: string
}

interface Props {
    data: CardData | null,
    isProcessing: boolean,
    user: any | null,
    fetch: Function,
    dialogOpen: Function
}

const UserIeCard: React.FC<Props> = (props) => {
    const packagesPurchaseEnabled = true;
    const [benefitsVisible, setBenefitsVisible] = React.useState(false);
    const [benefitDetailVisible, setBenefitDetailVisible] = React.useState(false);
    const [selectedBenefit, setSelectedBenefit] = React.useState<Benefit | null>(null);

    function onBenefitsClick() {
        setBenefitsVisible(!benefitsVisible);
    }

    function onBenefitItemClick(benefit: Benefit) {
        setBenefitDetailVisible(true);
        setSelectedBenefit(benefit);
        setBenefitsVisible(false);
    }

    function closeBenefitDetail() {
        setBenefitDetailVisible(false);
        setSelectedBenefit(null);
        setBenefitsVisible(true);
    }

    useEffect(() => {
        if (props.user) props.fetch();
    }, [props.user]);

    useEffect(() => {
        if (!props.data || props.data.error) return;

        if (document.location.hash === '#mis-beneficios') {
            setBenefitsVisible(true);
        }
    }, [props.data]);


    if (!props.user) return <Error404Page/>;
    if (props.isProcessing) return '';

    const {data} = props;

    if (!data) return '';

    if (data.error) {
        return <ErrorPage message="Ocurrió un error obtener la información. Por favor, inténtalo nuevamente."/>;
    }

    return <Container>
        <CardContainer>
            <IeCardFront data={data} flip={false} />
            <BarCodeImage dangerouslySetInnerHTML={{__html:data.barcode_image}}/>
            <CardNumber>{data.card_number.split('').join(' ')}</CardNumber> {/* @todo Replace with Barcode component used in coupons and wallet */}
        </CardContainer>
        <ProgressBar>
            <VisitsFlag level="basic">{data.basic_required_visits}</VisitsFlag>
            <VisitsFlag level="gold">{data.gold_required_visits}</VisitsFlag>
            <VisitsFlag level="premium">{data.premium_required_visits}</VisitsFlag>
            <div className="progress-bar"><div className="progress" style={{width: `${data.visits * 100 / data.premium_required_visits}%`, background: data.color}}></div></div>
        </ProgressBar>
        <div className="d-flex justify-content-between">
            <Subtitle>Visitas acumuladas</Subtitle>
            <VisitsCount color={data.color}>
                <i className="fa-light fa-clapperboard"></i> {data.visits}
            </VisitsCount>
        </div>
        <RequiredVisits>
            <li><VisitsFlag level="basic">{data.basic_required_visits}</VisitsFlag> visitas necesarias para subir a Nivel Red</li>
            <li><VisitsFlag level="gold">{data.gold_required_visits}</VisitsFlag> visitas necesarias para subir a Nivel Gold</li>
            <li><VisitsFlag level="premium">{data.premium_required_visits}</VisitsFlag> visitas necesarias para subir a Nivel Platino</li>
        </RequiredVisits>
        {data.message ? <p>{data.message}</p> : ''}
        <div className="py-3">
            <IePointsBlock points={data.points}/>
        </div>
        {data.benefits.length ? <React.Fragment>
            <CardButton onClick={onBenefitsClick}>
                <i className="fa-light fa-crown"></i>
                <Subtitle>Beneficios</Subtitle>
                <p className="small">Da click para ver el listado</p>
            </CardButton>
            <BenefitsListComponent
                visible={benefitsVisible}
                onClose={onBenefitsClick}
                onBenefitClick={onBenefitItemClick}
                benefits={data.benefits}
                levelName={data.level_data.name}
                levelColor={data.color}
                levelType={data.level}
            />
            <BenefitDetail
                benefit={selectedBenefit}
                visible={benefitDetailVisible}
                onClose={closeBenefitDetail}
            />
        </React.Fragment> : ''}

        {packagesPurchaseEnabled && (
            <CardButton 
                onClick={() => window.location.href = loopPackagesRoute}>
                <TicketsIcon />
                <Subtitle>Venta de Paquetes</Subtitle>
                <p className="small">Compra aquí tus paquetes de boletos/combos</p>
            </CardButton>
        )}
    </Container>;
}

const mapStateToProps = state => ({
    data: getIeCardData(state),
    isProcessing: isUserStuffProcessing(state),
    user: isUserLogged(state)
});

const mapDispatchToProps = dispatch => ({
    dialogOpen: message => dispatch(dialogAlertEasyClose(message)),
    fetch: () => dispatch(userIeCardFetch())
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(props => <FullColumnLayout Content={UserIeCard} {...props} bgWhite headerTransparent footerContent={false} />);